import { Icon } from "components/icon";
import {
    ClientContactRelationship,
    ContactSearchHitFragmentFragment,
    ContactSearchHitType,
} from "generated/graphql";
import React, { useCallback } from "react";
import { getEntityIconName } from "util/entity-icons";

interface ContactSearchHitDisplayProps {
    mode: "mini" | "full";
    hit: ContactSearchHitFragmentFragment;
    onClick: () => void;
}

export const ContactSearchHitDisplay: React.FC<ContactSearchHitDisplayProps> = props => {
    const { mode, hit, onClick } = props;

    if (mode === "mini") {
        return <ContactSearchHitDisplayMini hit={hit} onClick={onClick} />;
    }

    return <ContactSearchHitDisplayFull hit={hit} onClick={onClick} />;
};

interface ContactSearchHitDisplayMiniProps {
    hit: ContactSearchHitFragmentFragment;
    onClick: () => void;
}

const ContactSearchHitDisplayMini: React.FC<ContactSearchHitDisplayMiniProps> = props => {
    const { hit, onClick } = props;

    const handleLinkClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onClick();
        },
        [onClick],
    );

    switch (hit.type) {
        case ContactSearchHitType.Client:
            return (
                <a href="#" onClick={handleLinkClick}>
                    <IconAndName
                        icon={getEntityIconName("CLIENT")}
                        name={hit.client.name}
                    />
                </a>
            );
        case ContactSearchHitType.BestContact:
            return (
                <a href="#" onClick={handleLinkClick}>
                    <IconAndName icon="08_ContactPerson" name={hit.contact.name} />
                </a>
            );
        case ContactSearchHitType.ClientContact:
            return (
                <a href="#" onClick={handleLinkClick}>
                    <IconAndName
                        icon={
                            hit.clientContact.relationship ===
                            ClientContactRelationship.MoreContact
                                ? "46_MoreContacts"
                                : "54_physician"
                        }
                        name={hit.contact.name}
                    />
                </a>
            );
        case ContactSearchHitType.Community:
            return (
                <a href="#" onClick={handleLinkClick}>
                    <IconAndName
                        icon={getEntityIconName("COMMUNITY")}
                        name={hit.community.name}
                    />
                </a>
            );
        case ContactSearchHitType.CommunityContact:
            return (
                <a href="#" onClick={handleLinkClick}>
                    <IconAndName icon="26_CommunityContacts" name={hit.contact.name} />
                </a>
            );
        case ContactSearchHitType.ReferenceContact:
            return (
                <a href="#" onClick={handleLinkClick}>
                    <IconAndName
                        icon={getEntityIconName("REFERENCECONTACT")}
                        name={hit.contact.name}
                    />
                </a>
            );
        case ContactSearchHitType.ReferenceBusiness:
            return (
                <a href="#" onClick={handleLinkClick}>
                    <IconAndName
                        icon={getEntityIconName("REFERENCEBUSINESS")}
                        name={hit.contact.name}
                    />
                </a>
            );
        default: {
            const exhaustiveCheck: never = hit.type;
            throw new Error(`Unhandled case: ${exhaustiveCheck}`);
        }
    }
};

interface ContactSearchHitDisplayFullProps {
    hit: ContactSearchHitFragmentFragment;
    onClick: () => void;
}

const ContactSearchHitDisplayFull: React.FC<ContactSearchHitDisplayFullProps> = props => {
    const { hit, onClick } = props;

    const handleLinkClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onClick();
        },
        [onClick],
    );

    const anchorClassName =
        "block p-4 space-y-2 rounded-md hover:bg-gray-100 border border-gray-200";

    switch (hit.type) {
        case ContactSearchHitType.Client:
            return (
                <a href="#" className={anchorClassName} onClick={handleLinkClick}>
                    <IconAndName
                        icon={getEntityIconName("CLIENT")}
                        name={hit.client.name}
                    />
                </a>
            );
        case ContactSearchHitType.BestContact:
            return (
                <a href="#" className={anchorClassName} onClick={handleLinkClick}>
                    <IconAndName icon="08_ContactPerson" name={hit.contact.name} />
                    <IconAndName
                        icon={getEntityIconName("CLIENT")}
                        name={hit.client.name}
                    />
                </a>
            );
        case ContactSearchHitType.ClientContact:
            return (
                <a href="#" className={anchorClassName} onClick={handleLinkClick}>
                    <IconAndName
                        icon={
                            hit.clientContact.relationship ===
                            ClientContactRelationship.MoreContact
                                ? "46_MoreContacts"
                                : "54_physician"
                        }
                        name={hit.contact.name}
                    />
                    <IconAndName
                        icon={getEntityIconName("CLIENT")}
                        name={hit.client.name}
                    />
                </a>
            );
        case ContactSearchHitType.Community:
            return (
                <a href="#" className={anchorClassName} onClick={handleLinkClick}>
                    <IconAndName
                        icon={getEntityIconName("COMMUNITY")}
                        name={hit.community.name}
                    />
                </a>
            );
        case ContactSearchHitType.CommunityContact:
            return (
                <a href="#" className={anchorClassName} onClick={handleLinkClick}>
                    <IconAndName icon="26_CommunityContacts" name={hit.contact.name} />
                    <IconAndName
                        icon={getEntityIconName("COMMUNITY")}
                        name={hit.community.name}
                    />
                </a>
            );
        case ContactSearchHitType.ReferenceContact:
            return (
                <a href="#" className={anchorClassName} onClick={handleLinkClick}>
                    <IconAndName
                        icon={getEntityIconName("REFERENCECONTACT")}
                        name={hit.contact.name}
                    />
                    {hit.referenceBusiness ? (
                        <IconAndName
                            icon={getEntityIconName("REFERENCEBUSINESS")}
                            name={hit.referenceBusiness.businessContactEntity?.name}
                        />
                    ) : null}
                    {hit.community ? (
                        <IconAndName
                            icon={getEntityIconName("COMMUNITY")}
                            name={hit.community.name}
                        />
                    ) : null}
                </a>
            );
        case ContactSearchHitType.ReferenceBusiness:
            return (
                <a href="#" className={anchorClassName} onClick={handleLinkClick}>
                    <IconAndName
                        icon={getEntityIconName("REFERENCEBUSINESS")}
                        name={hit.contact.name}
                    />
                </a>
            );
        default: {
            const exhaustiveCheck: never = hit.type;
            throw new Error(`Unhandled case: ${exhaustiveCheck}`);
        }
    }
};

interface IconAndNameProps {
    icon: string;
    name: string;
}

const IconAndName: React.FC<IconAndNameProps> = props => {
    const { icon, name } = props;

    return (
        <div className="flex items-center space-x-2">
            <div className="w-4">
                <Icon name={icon} />
            </div>
            <span>{name || "(no name)"}</span>
        </div>
    );
};
